/**
 * Projectors for the opening sections: instructions, personal information,
 * citizenship, program eligibility, and components of interest.
 *
 * WHAT MAKES THESE RISKY
 *
 * Three of the five are CONDITIONAL, and conditional sections are where a
 * read-only view most easily lies about the applicant. Citizenship branches on
 * `isUsCitizen` and stores fields belonging to the branch not taken as simply
 * absent; program eligibility is a discriminated union whose two arms share
 * almost no fields. Projecting every field regardless of branch would show a
 * U.S. citizen a "Statutory eligibility basis: Not provided" row — inventing an
 * unanswered question out of one that was never asked. So each projector renders
 * the branch the applicant was actually on, and uses `'not_applicable'` where a
 * field belongs to the section but not to this answer.
 */

import type { CitizenshipInfo, IComponentsOfInterest, IOasisApplicationBase, ProgramEligibility } from '../../oasis-application.types';
import { LAW_STUDENT_YEAR_LABELS } from '../../oasis-application.types';
import {
	ACKNOWLEDGEMENT_LABELS,
	HOW_LEARNED_LABELS,
	NON_CITIZEN_ELIGIBILITY_LABELS,
	PROGRAM_LABELS,
	SLIP_TRACK_LABELS,
} from '../oasis-display-labels';
import { formatAddress, formatPhone } from '../oasis-display-format';
import type { DisplayBlock, DisplayField } from '../oasis-display.types';
import { affirmationBlock } from './checklist';
import { dateField, definedOnly, enumField, field, isBlank, textField, yesNoField } from './field-builders';

// ─── Instructions & acknowledgements ──────────────────────────────────────────

export function projectInstructions(data: IOasisApplicationBase['instructionsAndAcknowledgements']): DisplayBlock[] {
	return [
		{
			kind: 'fields',
			fields: [enumField('How the applicant learned about the program', data.howLearnedAboutProgram, HOW_LEARNED_LABELS)],
		},
		affirmationBlock('Acknowledgements', data.acknowledgements, ACKNOWLEDGEMENT_LABELS),
	];
}

// ─── Personal information ─────────────────────────────────────────────────────

/**
 * NOTE: `confirmEmailAddress` is deliberately not rendered. It is a form-input
 * artifact — a typo guard whose only job is to match `emailAddress` at entry
 * time. Showing it back would present the same address twice and imply it is
 * separate information.
 */
export function projectPersonalInformation(data: IOasisApplicationBase['personalInformation']): DisplayBlock[] {
	const name = [data.firstName, data.middleName, data.lastName]
		.map((part) => part?.trim())
		.filter((part) => !!part)
		.join(' ');

	const address = formatAddress(data);

	return [
		{
			kind: 'fields',
			fields: [
				textField('Name', name),
				textField('Address', address),
				textField('Phone', formatPhone(data.phoneNumber)),
				textField('Email', data.emailAddress),
			],
		},
	];
}

// ─── Citizenship ──────────────────────────────────────────────────────────────

export function projectCitizenship(data: CitizenshipInfo): DisplayBlock[] {
	const fields: DisplayField[] = [yesNoField('U.S. citizen', data.isUsCitizen)];

	if (data.isUsCitizen === 'yes') {
		fields.push(yesNoField('Dual citizen', data.isDualCitizen));
		// Only asked of dual citizens; for everyone else there is no such country.
		fields.push(
			textField('Other country of citizenship', data.dualCitizenshipCountry, data.isDualCitizen === 'yes' ? 'not_provided' : 'not_applicable'),
		);
	} else if (data.isUsCitizen === 'no') {
		fields.push(enumField('Statutory eligibility basis', data.nonCitizenEligibility, NON_CITIZEN_ELIGIBILITY_LABELS));
		// Collected only from non-citizens who meet a criterion, so "none" means
		// the question was never put to them rather than skipped.
		fields.push(
			textField(
				'Country of citizenship',
				data.citizenshipCountry,
				data.nonCitizenEligibility && data.nonCitizenEligibility !== 'none' ? 'not_provided' : 'not_applicable',
			),
		);
	}

	return [{ kind: 'fields', fields }];
}

// ─── Program eligibility ──────────────────────────────────────────────────────

/**
 * Program eligibility is NOT a field on `IOasisApplicationBase` — it lives on the
 * `OasisApplication` discriminated union alongside `program` and `slipTrack`.
 * That is why the internal detail page omitted the section entirely: reviewers
 * could not see which program an applicant was routed into or their year of
 * study. The projector therefore takes the eligibility slice and the program
 * discriminants explicitly rather than reading them off the base body.
 */
export function projectProgramEligibility(eligibility: ProgramEligibility | undefined, program?: string, slipTrack?: string): DisplayBlock[] {
	const blocks: DisplayBlock[] = [];

	const programFields = definedOnly([
		enumField('Program', program as 'honors_program' | 'slip' | undefined, PROGRAM_LABELS),
		enumField('SLIP track', slipTrack as 'slip_pathways' | 'slip_law_clerk' | undefined, SLIP_TRACK_LABELS),
	]);
	if (programFields.length) blocks.push({ kind: 'fields', fields: programFields });

	if (!eligibility) return blocks;

	if (eligibility.applicantStatus === 'current_law_student') {
		const fields: DisplayField[] = [
			field('Applicant status', { kind: 'text', value: 'Current law student' }),
			enumField('Law school enrollment', eligibility.lawSchoolEnrollmentStatus, {
				full_time: 'Full-time',
				part_time: 'Part-time',
			}),
			enumField('Current year', eligibility.currentYear, LAW_STUDENT_YEAR_LABELS),
			dateField('Expected J.D. date', eligibility.expectedJDDate),
		];

		const joint = eligibility.jointDegree;
		if (joint?.isJointDegreeStudent) {
			fields.push(
				field('Joint degree student', { kind: 'text', value: 'Yes' }),
				textField('Second degree type', joint.secondDegreeType),
				textField('Second degree school', joint.secondDegreeSchool),
				dateField('Second degree expected completion', joint.secondDegreeExpectedCompletion),
				yesNoField('Completed J.D.', joint.hasCompletedJD),
				yesNoField('Completed second degree', joint.hasCompletedSecondDegree),
			);
		} else {
			fields.push(field('Joint degree student', { kind: 'text', value: 'No' }));
		}

		blocks.push({ kind: 'fields', fields });
	} else {
		blocks.push({
			kind: 'fields',
			fields: [
				field('Applicant status', { kind: 'text', value: 'Law school graduate' }),
				dateField('J.D. completion date', eligibility.jdCompletionDate),
			],
		});
	}

	return blocks;
}

/** Drops blank badges so a card never renders an empty tag. */
export function definedBadges(badges: readonly (string | undefined)[]): string[] {
	return badges.filter((b): b is string => !isBlank(b));
}

// ─── Components of interest ───────────────────────────────────────────────────

/**
 * Selections are a SNAPSHOT taken at application time — the hiring-office catalog
 * is rebuilt every cycle, so the stored label is authoritative and must be shown
 * as stored rather than looked up against a current catalog that may have
 * renamed or dropped the office.
 */
export function projectComponentsOfInterest(data: IComponentsOfInterest): DisplayBlock[] {
	return [
		{
			kind: 'entries',
			entries: (data.selections ?? []).map((selection) => ({
				title: selection.label || selection.code,
				subtitle: selection.code,
				badges: definedBadges([
					PROGRAM_LABELS[selection.program],
					selection.track ? SLIP_TRACK_LABELS[selection.track] : undefined,
					selection.participation ? (selection.participation === 'formal' ? 'Formal' : 'Informal') : undefined,
				]),
				fields: definedOnly([textField('Anticipated hires', selection.anticipatedHires)]),
			})),
			emptyText: 'No offices selected.',
		},
	];
}
